/**
 * Admin Store
 *
 * Manages admin dashboard state: audit logs, flagged content, roles,
 * and verification queue.
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  AuditLogEntry,
  FlaggedContent,
  FlaggedContentStatus,
  AdminRoleAssignment,
  AdminUserDetail,
  AdminRole,
  ModerationActionType,
  UUID,
} from '../types'
import { adminApi } from '../api'
import type { VerificationItem } from '../api/admin.api'

interface AdminState {
  // Audit logs
  auditLogs: AuditLogEntry[]
  auditLogTotal: number
  auditLogPage: number

  // Flagged content
  flaggedContent: FlaggedContent[]
  flagFilter: FlaggedContentStatus | null

  // User detail
  selectedUser: AdminUserDetail | null

  // Admin roles
  adminRoles: AdminRoleAssignment[]

  // Verification queue
  verificationQueue: VerificationItem[]
  isLoadingVerifications: boolean

  // UI
  isLoading: boolean
  error: string | null

  // Actions
  fetchAuditLogs: (params?: { page?: number; action?: string; resourceType?: string }) => Promise<void>
  fetchFlaggedContent: (status?: FlaggedContentStatus) => Promise<void>
  resolveFlag: (flagId: UUID, action: ModerationActionType, resolution?: string) => Promise<void>
  fetchUserDetail: (userId: UUID) => Promise<void>
  clearSelectedUser: () => void
  fetchAdminRoles: () => Promise<void>
  assignRole: (userId: UUID, role: AdminRole) => Promise<void>
  removeRole: (userId: UUID) => Promise<void>

  // Verification actions
  fetchVerificationQueue: () => Promise<void>
  approveVerification: (trackRecordId: UUID) => Promise<boolean>
  rejectVerification: (trackRecordId: UUID, reason: string, note?: string) => Promise<boolean>
}

export const useAdminStore = createWithEqualityFn<AdminState>((set, get) => ({
  auditLogs: [],
  auditLogTotal: 0,
  auditLogPage: 1,
  flaggedContent: [],
  flagFilter: null,
  selectedUser: null,
  adminRoles: [],
  verificationQueue: [],
  isLoadingVerifications: false,
  isLoading: false,
  error: null,

  fetchAuditLogs: async (params) => {
    set({ isLoading: true, error: null })
    const response = await adminApi.getAuditLogs({
      page: params?.page ?? 1,
      limit: 20,
      action: params?.action,
      resourceType: params?.resourceType,
    })
    if (response.success && response.data) {
      set({
        auditLogs: response.data,
        auditLogTotal: response.pagination?.total ?? 0,
        auditLogPage: params?.page ?? 1,
        isLoading: false,
      })
    } else {
      set({ error: 'Failed to load audit logs', isLoading: false })
    }
  },

  fetchFlaggedContent: async (status) => {
    set({ isLoading: true, error: null, flagFilter: status ?? null })
    const response = await adminApi.getFlaggedContent(status)
    if (response.success && response.data) {
      set({ flaggedContent: response.data, isLoading: false })
    } else {
      set({ error: 'Failed to load flagged content', isLoading: false })
    }
  },

  resolveFlag: async (flagId, action, resolution) => {
    set({ isLoading: true })
    const response = await adminApi.resolveFlag(flagId, action, resolution)
    if (response.success) {
      // Refresh flagged content
      await get().fetchFlaggedContent(get().flagFilter ?? undefined)
    } else {
      set({ error: response.error ?? 'Failed to resolve flag', isLoading: false })
    }
  },

  fetchUserDetail: async (userId) => {
    set({ isLoading: true, error: null })
    const response = await adminApi.getUserDetail(userId)
    if (response.success && response.data) {
      set({ selectedUser: response.data, isLoading: false })
    } else {
      set({ error: response.error ?? 'Failed to load user', isLoading: false })
    }
  },

  clearSelectedUser: () => {
    set({ selectedUser: null })
  },

  fetchAdminRoles: async () => {
    set({ isLoading: true })
    const response = await adminApi.getAdminRoles()
    if (response.success && response.data) {
      set({ adminRoles: response.data, isLoading: false })
    } else {
      set({ isLoading: false })
    }
  },

  assignRole: async (userId, role) => {
    set({ isLoading: true })
    const response = await adminApi.assignAdminRole(userId, role)
    if (response.success) {
      await get().fetchAdminRoles()
    } else {
      set({ error: response.error ?? 'Failed to assign role', isLoading: false })
    }
  },

  removeRole: async (userId) => {
    set({ isLoading: true })
    const response = await adminApi.removeAdminRole(userId)
    if (response.success) {
      await get().fetchAdminRoles()
    } else {
      set({ error: response.error ?? 'Failed to remove role', isLoading: false })
    }
  },

  // ============================================================================
  // Verification Queue
  // ============================================================================

  fetchVerificationQueue: async () => {
    set({ isLoadingVerifications: true, error: null })
    const response = await adminApi.getPendingVerifications()
    if (response.success && response.data) {
      set({ verificationQueue: response.data as VerificationItem[], isLoadingVerifications: false })
    } else {
      set({ error: 'Failed to load verification queue', isLoadingVerifications: false })
    }
  },

  approveVerification: async (trackRecordId) => {
    set({ isLoading: true, error: null })
    const response = await adminApi.approveVerification(trackRecordId)
    if (response.success) {
      // Remove from local queue
      set((state) => ({
        verificationQueue: state.verificationQueue.filter(v => v.id !== trackRecordId),
        isLoading: false,
      }))
      return true
    } else {
      set({ error: response.error ?? 'Failed to approve verification', isLoading: false })
      return false
    }
  },

  rejectVerification: async (trackRecordId, reason, note) => {
    set({ isLoading: true, error: null })
    const response = await adminApi.rejectVerification(trackRecordId, reason, note)
    if (response.success) {
      // Remove from local queue
      set((state) => ({
        verificationQueue: state.verificationQueue.filter(v => v.id !== trackRecordId),
        isLoading: false,
      }))
      return true
    } else {
      set({ error: response.error ?? 'Failed to reject verification', isLoading: false })
      return false
    }
  },
}))

export const selectAuditLogs = (state: AdminState) => state.auditLogs
export const selectFlaggedContent = (state: AdminState) => state.flaggedContent
export const selectSelectedUser = (state: AdminState) => state.selectedUser
export const selectAdminRoles = (state: AdminState) => state.adminRoles
export const selectVerificationQueue = (state: AdminState) => state.verificationQueue
